import { useRef, useState } from 'react';
import { Link, useParams, Navigate } from '@/lib/router-compat';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/stores/cartStore';
import { ShopifyProduct } from '@/lib/shopify';
import { useShopifyProducts } from '@/hooks/useShopifyProducts';
import { getProduct, getAccessories } from '@/data/products';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Check, Truck, RotateCcw, Shield, Clock, Minus, Plus } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import StickyAddToCartBar from '@/components/product/StickyAddToCartBar';
import {
  accessoryImageAssets,
  accessoryGalleryAssets,
  accessoryVariantGalleryAssets,
} from '@/data/productImages';
import { imageAlt } from '@/config/imageCatalog';
import { findShopifyProductBySlug } from '@/lib/matchShopifyProduct';
import AccessoryProductJsonLd from '@/components/AccessoryProductJsonLd';
import { getEuroPrice, formatEuroPrice, isAvailable } from '@/seo/merchantProduct';

/** Maps localized accessory slugs (FR + EN) back to NL product keys */
const localizedSlugToNl: Record<string, string> = {
  'bouchons-oreilles': 'oordoppen',
  'filtres-musicaux': 'muziekfilters',
  'etui-rangement': 'bewaarkoker',
  'cordon': 'nekkoord',
  'earplugs': 'oordoppen',
  'music-filters': 'muziekfilters',
  'storage-case': 'bewaarkoker',
  'cord': 'nekkoord',
};

const AccessoryDetailPage = ({ initialProducts = [] }: { initialProducts?: ShopifyProduct[] }) => {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slug = rawSlug ? (localizedSlugToNl[rawSlug] ?? rawSlug) : undefined;
  const product = getProduct(slug ?? '');
  const { addItem, isLoading } = useCartStore();
  const { t, locale, localePath } = useLanguage();

  const translatedData = product ? t.acc.products[product.slug as keyof typeof t.acc.products] : undefined;
  const [selectedVariant, setSelectedVariant] = useState<string | null>(
    translatedData?.defaultVariant ?? product?.variants?.options[0]?.value ?? null
  );
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const purchaseAnchorRef = useRef<HTMLDivElement>(null);

  const { data: liveProducts } = useShopifyProducts();
  const shopifyProducts = liveProducts ?? initialProducts;

  const metaData = slug ? t.meta.accessoryDetail[slug as keyof typeof t.meta.accessoryDetail] : undefined;
  usePageMeta(
    metaData?.title ?? `${translatedData?.name ?? product?.name ?? 'Accessoire'} – Rave Safe`,
    metaData?.description ?? translatedData?.description ?? product?.description ?? ''
  );

  if (!product || product.category !== 'accessoire') {
    return <Navigate to={localePath('/accessoires')} replace />;
  }

  const translated = t.acc.products[product.slug as keyof typeof t.acc.products];
  const name = translated?.name ?? product.name;
  const description = translated?.description ?? product.description;
  const longDescription = translated?.longDescription ?? description;
  const features = translated?.features ?? product.features;
  const variantLabel = translated?.variantLabel ?? product.variants?.label;
  const variantOptions = product.variants?.options.map((opt) => ({
    value: opt.value,
    label: translated?.variantOptions?.[opt.value] ?? opt.label,
  }));

  const shopifyProduct = findShopifyProductBySlug(shopifyProducts, product.slug);

  const getShopifyVariant = () => {
    if (!shopifyProduct) return null;
    if (!selectedVariant) return shopifyProduct.node.variants.edges[0]?.node;
    const match = shopifyProduct.node.variants.edges.find(v =>
      v.node.selectedOptions.some(o => o.value === selectedVariant || o.value === `${selectedVariant}`)
    );
    return match?.node ?? shopifyProduct.node.variants.edges[0]?.node;
  };

  const shopifyVariant = getShopifyVariant();
  const priceText = formatEuroPrice(getEuroPrice(shopifyVariant), locale);
  const inStock = isAvailable(shopifyVariant);
  const gallery = (selectedVariant ? accessoryVariantGalleryAssets[product.slug]?.[selectedVariant] : undefined) ?? accessoryGalleryAssets[product.slug] ?? (accessoryImageAssets[product.slug] ? [accessoryImageAssets[product.slug]] : []);

  const handleAdd = async () => {
    if (!shopifyProduct || !shopifyVariant || !inStock) return;
    const suffix = selectedVariant
      ? ` – ${variantOptions?.find((o) => o.value === selectedVariant)?.label ?? selectedVariant}`
      : '';
    await addItem({
      product: shopifyProduct,
      variantId: shopifyVariant.id,
      variantTitle: shopifyVariant.title,
      price: shopifyVariant.price,
      quantity,
      selectedOptions: shopifyVariant.selectedOptions || [],
    });
    toast.success(`${quantity}x ${name}${suffix} ${t.product.toastAdded}`);
  };

  const otherAccessories = getAccessories().filter((a) => a.id !== product.id);
  const selectedVariantLabel = selectedVariant
    ? variantOptions?.find((option) => option.value === selectedVariant)?.label ?? selectedVariant
    : undefined;

  return (
    <div className="py-12 sm:py-20">
      <AccessoryProductJsonLd product={product} name={name} description={description} variant={shopifyVariant} visibleImages={gallery.map(image => image.src)} />
      <div className="container mx-auto px-4">
        <nav className="text-sm text-muted-foreground mb-8">
          <Link to={localePath('/')} className="hover:text-primary transition-colors">
            {t.common.home}
          </Link>
          <span className="mx-2">/</span>
          <Link to={localePath('/accessoires')} className="hover:text-primary transition-colors">
            {t.acc.title}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-16">
          <div>
            {(() => {
              if (gallery.length === 0) {
                return (
                  <div className="bg-secondary rounded-2xl border border-border flex items-center justify-center aspect-square">
                    <span className="text-muted-foreground font-medium text-lg">{name}</span>
                  </div>
                );
              }
              const activeAsset = gallery[activeImage] ?? gallery[0];
              return (
                <>
                  <div className="relative bg-card rounded-2xl border border-border overflow-hidden mb-3">
                    <img key={activeImage} src={activeAsset.src} alt={imageAlt(activeAsset, locale)} className={`w-full aspect-square object-cover ${activeImage > 0 ? 'motion-fade-in' : ''}`} width={600} height={600} decoding="async" fetchPriority="high" />
                  </div>
                  {gallery.length > 1 && (
                    <div className="flex gap-3">
                      {gallery.map((asset, i) => (
                        <button
                          key={asset.filename}
                          onClick={() => setActiveImage(i)}
                          className={`w-20 h-20 rounded-lg border-2 overflow-hidden motion-thumb ${activeImage === i ? 'border-primary' : 'border-border hover:border-muted-foreground'}`}
                        >
                          <img src={asset.src} alt={imageAlt(asset, locale)} className="w-full h-full object-cover" width={80} height={80} loading="lazy" decoding="async" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="flex flex-col justify-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{name}</h1>

            {features.length > 0 && (
              <ul className="space-y-2 mb-6">
                {features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            )}

            {variantOptions && (
              <div className="mb-6">
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  {variantLabel}
                </label>
                <div className="flex flex-wrap gap-2">
                  {variantOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setSelectedVariant(opt.value); setActiveImage(0); }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        selectedVariant === opt.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/30'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-2">
              <span className="text-3xl font-bold text-primary">
                {priceText}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mb-6">
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${inStock ? 'bg-green-500' : 'bg-muted-foreground'}`} />
              <span className="text-sm text-muted-foreground">{shopifyVariant ? (inStock ? t.acc.detailPage.inStock : (locale === 'en' ? 'Out of stock' : 'Uitverkocht')) : (locale === 'en' ? 'Availability temporarily unknown' : 'Beschikbaarheid tijdelijk onbekend')}</span>
            </div>

            <div ref={purchaseAnchorRef} className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-center border border-border rounded-lg shrink-0">
                <button
                  type="button"
                  aria-label={t.product.decreaseQuantity}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="motion-press p-3 text-muted-foreground hover:text-foreground"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-4 font-semibold text-foreground min-w-[3rem] text-center" aria-live="polite">{quantity}</span>
                <button
                  type="button"
                  aria-label={t.product.increaseQuantity}
                  onClick={() => setQuantity((q) => q + 1)}
                  className="motion-press p-3 text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <Button variant="cta" size="lg" className="text-base font-semibold h-12 flex-1 min-w-0" onClick={handleAdd} disabled={isLoading || !inStock}>
                {isLoading ? '...' : t.acc.detailPage.addToCart}
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[Truck, RotateCcw, Shield, Clock].map((Icon, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <span>{t.acc.detailPage.usps[i]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {(translated?.htmlDescription || translated?.faq) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-16">
            {translated?.faq && translated.faq.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-4">{t.product.faqTitle}</h2>
                <Accordion type="single" collapsible className="w-full">
                  {translated.faq.map((item, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left text-sm font-medium text-foreground">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          className="text-sm text-muted-foreground leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80"
                          dangerouslySetInnerHTML={{ __html: item.a }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {translated?.htmlDescription && (
              <div
                className="prose prose-sm max-w-none text-muted-foreground [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-6 [&_h2]:mb-3 [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80 [&_strong]:text-foreground [&_ul]:space-y-1 [&_li]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-4 [&_table]:w-full [&_table]:text-sm [&_table]:mt-4 [&_table]:mb-4 [&_table]:block [&_table]:overflow-x-auto [&_table]:whitespace-nowrap [&_th]:text-left [&_th]:py-2 [&_th]:px-3 [&_th]:font-semibold [&_th]:text-foreground [&_th]:border-b [&_th]:border-border [&_td]:py-2 [&_td]:px-3 [&_td]:text-muted-foreground [&_td]:border-b [&_td]:border-border/50"
                dangerouslySetInnerHTML={{ __html: translated.htmlDescription }}
              />
            )}
          </div>
        )}

        {otherAccessories.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6">
              {t.acc.detailPage.relatedProducts}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherAccessories.map((acc) => {
                const accT = t.acc.products[acc.slug as keyof typeof t.acc.products];
                const accName = accT?.name ?? acc.name;
                const accImage = accessoryImageAssets[acc.slug];
                return (
                  <Link
                    key={acc.id}
                    to={localePath(`/accessoires/${acc.slug}`)}
                    className="bg-card rounded-xl border border-border p-5 hover:border-primary/30 motion-card"
                  >
                    <div className="h-32 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                      {accImage ? (
                        <img src={accImage.src} alt={imageAlt(accImage, locale)} className="motion-card-img h-full object-contain" width={128} height={128} loading="lazy" decoding="async" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{accName}</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{accName}</h3>
                    <span className="font-bold text-primary">
                      {formatEuroPrice(getEuroPrice(findShopifyProductBySlug(shopifyProducts, acc.slug)?.node.variants.edges[0]?.node), locale)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <StickyAddToCartBar
        anchorRef={purchaseAnchorRef}
        productName={name}
        price={priceText}
        variantLabel={selectedVariantLabel ? `${variantLabel ?? ''}${variantLabel ? ': ' : ''}${selectedVariantLabel}` : undefined}
        quantity={quantity}
        setQuantity={setQuantity}
        onAdd={handleAdd}
        isLoading={isLoading}
        disabled={!inStock}
        addLabel={t.acc.detailPage.addToCart}
        decreaseLabel={t.product.decreaseQuantity}
        increaseLabel={t.product.increaseQuantity}
      />
    </div>
  );
};

export default AccessoryDetailPage;
